<template>
  <div class="course-page">
    <div class="course-page__inner">
      <CourseHeader
        :eyebrow="$t('courseIndex.teacherEyebrow')"
        :title="$t('courseIndex.materialsTitle')"
        :subtitle="$t('courseIndex.materialsSub')"
        :show-ramp="false"
      />

      <!-- Cards 86akqwgcv / 86akqwgkw: filtro por nível e busca de aula. -->
      <CourseFinder
        :search.sync="search"
        :level.sync="level"
        :levels="levelFilters"
        :summary="summary"
      />

      <!-- carregando -->
      <page-skeleton v-if="loading" variant="cards" class="mt-4" />

      <template v-else>
        <LessonSearchResults
          v-if="searching"
          :results="foundLessons"
          @open="openLesson"
        />

        <section v-if="visibleModules.length > 0" class="msection">
          <p v-if="searching" class="msection__label">
            {{ $t("courseIndex.modules") }}
          </p>
          <div class="mgrid">
            <ModuleCard
              v-for="(item, index) in visibleModules"
              :key="item.id || index"
              :course="item"
              role="teacher"
              @open="goToCourse"
            />
          </div>
        </section>

        <!-- vazio -->
        <div v-else-if="!searching" class="course-page__state">
          <span v-if="level !== null">{{
            $t("courseIndex.noModulesLevel")
          }}</span>
          <span v-else>{{ $t("courseIndex.noModules") }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import pitchPartnerApi from "@/shared/pitchPartnerApi";
import {
  isTrainingModule,
  LEVEL_FILTERS,
  matchesLevelFilter,
  normalizeSearch,
  findLessons,
} from "@/shared/courseTiers";
import CourseHeader from "@/components/connect/course/CourseHeader.vue";
import CourseFinder from "@/components/connect/course/CourseFinder.vue";
import LessonSearchResults from "@/components/connect/course/LessonSearchResults.vue";
import ModuleCard from "@/components/connect/course/ModuleCard.vue";

export default {
  name: "CourseIndex",
  components: {
    CourseHeader,
    CourseFinder,
    LessonSearchResults,
    ModuleCard,
  },
  async created() {
    try {
      const res = await pitchPartnerApi.listCourseContent();
      this.content = res.data.content || [];
      this.count = res.data.count;
    } catch (error) {
      this.$root.$emit("display-alert", {
        text: this.$t("courseIndex.modulesFailed"),
        type: 1,
      });
    }

    this.loading = false;
  },
  data() {
    return {
      loading: true,
      content: [],
      count: 0,
      search: "",
      level: null,
    };
  },
  computed: {
    // Card 86akqwmzy: os treinamentos (módulos só de professores) saíram
    // daqui para a aba Treinamentos.
    modules() {
      return this.content.filter((item) => !isTrainingModule(item));
    },
    levelFilters() {
      // As trilhas à parte só aparecem se houver módulo delas.
      return LEVEL_FILTERS.filter(
        (filter) =>
          !filter.track ||
          this.modules.some((item) => matchesLevelFilter(item, filter))
      );
    },
    activeLevel() {
      return this.level === null ? null : this.levelFilters[this.level];
    },
    levelModules() {
      return this.modules.filter((item) =>
        matchesLevelFilter(item, this.activeLevel)
      );
    },
    searching() {
      return !!normalizeSearch(this.search);
    },
    foundLessons() {
      return findLessons(this.levelModules, this.search);
    },
    // Na busca, ficam os módulos cujo título bate ou que têm aula encontrada.
    visibleModules() {
      if (!this.searching) return this.levelModules;

      const term = normalizeSearch(this.search);
      const withLesson = new Set(
        this.foundLessons.map((item) => String(item.module.id))
      );
      return this.levelModules.filter(
        (item) =>
          withLesson.has(String(item.id)) ||
          normalizeSearch(item.title).includes(term)
      );
    },
    summary() {
      if (this.loading) return "";
      const total = this.levelModules.length;
      const label = this.$tc("courseIndex.moduleCount", total, {
        count: total,
      });
      return this.activeLevel ? `${label} · ${this.activeLevel.key}` : label;
    },
  },
  methods: {
    goToCourse(item) {
      this.$router.push({
        path: "/connect/professor/curso/" + item.id,
        params: { id: item.id },
      });
    },
    openLesson({ module, lesson }) {
      this.$router.push({
        path: `/connect/professor/curso/${module.id}/${lesson.id}`,
      });
    },
  },
};
</script>

<style scoped>
.course-page {
  padding: clamp(20px, 4vw, 44px) clamp(16px, 4vw, 40px) 72px;
}
.course-page__inner {
  max-width: 1180px;
  margin: 0 auto;
}
.msection + .msection {
  margin-top: 44px;
}
.msection__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px 20px;
  margin-bottom: 18px;
  font-family: "Poppins", sans-serif;
}
.msection__label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--dk-faint, #9a96a6);
}
.msection__hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--dk-ink-soft, #6e6a79);
}
.msection__stat {
  margin: 0;
  font-size: 13px;
  color: var(--dk-ink-soft, #6e6a79);
}
.msection__stat strong {
  color: var(--dk-ink, #211b2e);
}
.mgrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
@media (max-width: 900px) {
  .mgrid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .mgrid {
    grid-template-columns: 1fr;
  }
}
.course-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  font-family: "Poppins", sans-serif;
  color: var(--dk-ink-soft, #6e6a79);
}
</style>
